import invariant from "invariant";
import entityFieldMetaStore, { getFieldInfoComparator } from "data/meta/entityFieldMetaStore";
import TableColumnMetaStore from "data/meta/TableColumnMetaStore";
import { optionName } from "data/selectors/options";
import TableProxy from "data/util/TableProxy";
import entityMetaStore from "data/meta/entityMetaStore";
import {
    comparatorForEntity,
    comparatorForField,
} from "jsmodel/compareRecords";
import { infoTypeToFieldType } from "data/util/infoTypeToFieldType";
import asArray from "data/util/asArray";
import recordLabel from "data/selectors/recordLabel";

// QUACK! FieldInfo's and EntityInfo's scripts are shaped the same!
const hasScript = name => info =>
    info.scripts && info.scripts.hasOwnProperty(name) && !!info.scripts[name];

export const hasRecalcScript = hasScript("recalc");

export const hasCandidateFilter = hasScript("filter");

export const hasEnableScript = hasScript("enable");

export const hasFilterScript = hasScript("filter");

export const hasIsVersionScript = hasScript("isVersion");

export const isAutoManaged = fieldInfo =>
    fieldInfo.autoManageKey != null;

export const isInverseAssoc = fieldInfo =>
    fieldInfo.type === "InverseAssociationField";

export const isAbstractAssoc = fieldInfo =>
    fieldInfo.type === "AssociationField" ||
    isInverseAssoc(fieldInfo) ||
    isComposition(fieldInfo);

export const isComposition = fieldInfo =>
    fieldInfo.type === "CompositionField";

export const isChoice = fieldInfo =>
    fieldInfo.type === "ChoiceField";

export const isFile = fieldInfo =>
    fieldInfo.type === "FileField";

export const isSelection = fieldInfo =>
    isChoice(fieldInfo) || isAbstractAssoc(fieldInfo);

/**
 * I proxy Store data as a stateful object w/ a `get` method, like Backbone's
 * Model has, and which is needed for passing into AS scriptlets.
 */
class EntityProxy {

    /**
     * I accept _either_ (TableAccess, entityKey, tableKey, id), (TableAccess,
     * entityInfo, id), _or_ (TableProxy, entityKey). Javascript provides no way
     * to gracefully expose multiple constructors.
     * @param access a TableAccess or TableProxy to use for data access
     * @param entityKey key or info for the entity to masquerade as
     * @param tableKey the key of the table under the entity; only needed if a
     *  TableAccess is provided and entity info is not.
     * @param id the id of the record being proxied; only needed if a
     *  TableAccess is provided.
     */
    constructor(access, entityKey, tableKey, id) {
        // "overload" the constructor
        if (entityKey == null) {
            throw new Error(`Proxies cannot be constructed with null keys or ids ('${entityKey}#${id}').`);
        }
        if (entityKey.hasOwnProperty("key")) {
            // it's an info object
            this.entityInfo = entityKey;
            entityKey = this.entityInfo.key;
            id = tableKey;
            tableKey = this.entityInfo.tableKey;
        } else {
            this.entityInfo = entityMetaStore.getInfo(entityKey);
        }
        if (access instanceof TableProxy) {
            this.tableProxy = access;
            this.entityKey = entityKey;
            this.id = access.id;
        } else {
            if (tableKey == null) {
                tableKey = this.entityInfo.tableKey;
            }
            if (tableKey == null || id == null) {
                throw new Error(`Proxies cannot be constructed with null keys or ids ('${entityKey}:${tableKey}#${id}').`);
            }
            this.tableProxy = new TableProxy(access, tableKey, id);
            this.entityKey = entityKey;
            this.id = id;
        }
    }

    get(field) {
        try {
            return this.getField(field);
        } catch (e) {
            console.warn("EP.get: " + this.entityKey + "#" + this.id + "." + field, e);
            return null;
        }
    }

    isFieldEnabled(fieldOrInfo) {
        const info = typeof fieldOrInfo === "string"
            ? entityFieldMetaStore.getInfo(this.entityKey, fieldOrInfo)
            : fieldOrInfo;
        invariant(
            this.entityKey === info.entityKey,
            "Field '%s' belongs to '%s', not '%s'",
            info.key,
            info.entityKey,
            this.entityKey,
        );
        if (!hasEnableScript(info)) return true;
        try {
            const enabled = info.scripts.enable(this);
            return enabled == null || enabled;
        } catch (e) {
            console.warn("ENABLED: " + this.entityKey + "#" + this.id + "." + info.key, e);
            return true;
        }
    }

    isRecordEnabled() {
        if (! hasEnableScript(this.entityInfo)) return true;
        try {
            const enabled = this.entityInfo.scripts.enable(this);
            return enabled == null || enabled;
        } catch (e) {
            console.warn("ENABLED: " + this.entityKey + "#" + this.id, e);
            return true;
        }
    }

    frontsColumnWithKey(colKey) {
        // first, ensure it's even a legal column
        TableColumnMetaStore.getInfoByKey(this.tableProxy.tableKey, colKey);
        // todo: this should probably cache column mappings
        return entityFieldMetaStore.getInfoForColumnKey(
            this.entityKey,
            colKey,
            false) != null;
    }

    getAttributeType(fieldOrInfo) {
        const info = this._toInfo(fieldOrInfo);
        return infoTypeToFieldType(info.type);
    }

    _toInfo(fieldOrInfo) {
        if (typeof fieldOrInfo === "string") {
            // todo: this should probably cache entity-table mappings
            return entityFieldMetaStore.getInfo(this.entityKey, fieldOrInfo);
        } else {
            return fieldOrInfo;
        }
    }

    getField(fieldOrInfo) {
        const info = this._toInfo(fieldOrInfo);
        let value = this.tableProxy.getColumn(info.logicalKey, true);
        if (value == null) return null;
        if (isAbstractAssoc(info)) {
            const colInfo = TableColumnMetaStore.getInfoByKey(this.tableProxy.tableKey, info.logicalKey);
            value = asArray(value).map(id =>
                new EntityProxy(this.tableProxy.access, info.otherEntityKey, colInfo.otherTableKey, id));

            if (value.length > 1) {
                const comparator = comparatorForField(info);
                if (comparator) {
                    value.sort(comparator);
                }
            }
        } else if (isChoice(info)) {
            value = asArray(value).map(
                optionName(info.choiceKey));
        }
        if (isSelection(info)) {
            value = info.multiValued
                ? value
                : value.length > 0
                    ? value[0]
                    : null;
        }
        return value;
    }

    compareTo(other) {
        if(this.entityKey !== other.entityKey) {
            throw new Error("'" + this.entityKey + "' is not of the same entity type '" + other.entityKey + "'");
        }
        if(this.id === other.id) {
            // identical quick return;
            return 0;
        }
        if (this._natural_order == null) {
            this._natural_order = comparatorForEntity(this.entityInfo);
        }
        return this._natural_order(this, other);
    }

    getColumnByKey(colKey) {
        return this.getField(
            entityFieldMetaStore.getInfoForColumnKey(this.entityKey, colKey).key
        );
    }

    getKey() {
        return this.entityKey + "#" + this.id;
    }

    getLabel() {
        return recordLabel(this.entityInfo, this);
    }

    getParent() {
        return this.entityInfo.parentPointerKey
            ? this.getField(this.entityInfo.parentPointerKey)
            : null;
    }

    getRecordMeta() {
        return this.tableProxy.getRecordMeta();
    }

    equals(o) {
        if (o == null) return false;
        if (!(o instanceof EntityProxy)) return false;
        if (this.entityKey !== o.entityKey) return false;
        if (!this.tableProxy.equals(o.tableProxy)) return false;
        return true;
    }

    // is this record still the type it was created as?
    isCorrectVersion() {
        if (this.entityInfo.type === "ROOT" && hasIsVersionScript(this.entityInfo)) {
            const v = this.entityInfo.scripts.isVersion(this);
            return v == null || !!v;
        }
        return true;
    }

    getFieldModelComparator(shortnameAttribute = "shortname") {
        return getFieldInfoComparator(
            entityFieldMetaStore.getInfosByKey(this.entityKey)
        , shortnameAttribute);
    }

    canCreate() {
        return this.entityInfo.canCreate;
    }

    canWrite() {
        return this.entityInfo.canWrite;
    }

    canDelete() {
        return this.entityInfo.canDelete;
    }

    getMetadata() {
        return this.tableProxy.getMetadata();
    }

}

export default EntityProxy;



// WEBPACK FOOTER //
// ./app/data/util/EntityProxy.js